<template>
  <router-view v-if="routedSong" />
  <template v-else>
    <transition-fade :duration="500" appear>
      <div v-if="activeSeasonContentForSchoolLevel" id="song-list-header">
        <h1
          class="title-block"
          :class="{
            'title-block-onderbouw':
              activeSeasonContentForSchoolLevel.schoolLevel.slug ===
              'onderbouw',
            'title-block-midden-bovenbouw':
              activeSeasonContentForSchoolLevel.schoolLevel.slug ===
              'midden-bovenbouw',
          }"
        >
          {{ activeVolume.title }} / {{ activeSeason.issueNumber }}
        </h1>
      </div>
    </transition-fade>
    <transition-fade :duration="500" appear>
      <div v-if="activeSeasonContentForSchoolLevel?.songs?.length">
        <ul
          ref="songList"
          id="song-list"
          class="content-start bg-white rounded-3xl flex flex-wrap flex-col max-h-[calc(100vh_-_240px)] justify-start mb-[-20px] overflow-x-auto py-[29px] pr-[28px] pl-[9px]"
          @scroll="onScroll"
        >
          <li
            v-for="song in activeSeasonContentForSchoolLevel.songs"
            :key="song.slug"
            class="song-list__song-item border-0 border-r-2 border-solid border-r-black/20 max-w-[398px] px-[10px]"
          >
            <router-link
              :to="{
                name: 'SongListKaraokePlayer',
                params: {
                  ...route.params,
                  songSlug: song.slug,
                },
              }"
              class="song-list__song-item__link items-center bg-white border-0 border-b border-solid border-b-green text-almostBlack flex justify-between py-[7px] px-[10px] no-underline scale-100 transition-all duration-300 ease-[cubic-bezier(.25,.8,.25,1)] hover:bg-hover hover:scale-x-105 hover:ease-[ease]"
            >
              <div
                class="song-list__song-item__title grow text-[17px] font-light py-0 pr-[20px] pl-[10px]"
              >
                {{ song.title }}
              </div>
              <div
                class="song-list__song-item__duration text-green text-[11px] tracking-[-0.4px]"
                :class="
                  activeSeasonContentForSchoolLevel.schoolLevel.slug ===
                  'onderbouw'
                    ? 'text-green'
                    : 'text-purple'
                "
              >
                {{
                  (song.vocalMix ?? song.karaokeSoundMix)?.durationFormatted ||
                  "00:00"
                }}
              </div>
            </router-link>
          </li>
        </ul>
        <MediaMusicCircle
          class="pointer-events-none absolute top-0 right-0 translate-x-[-20%] translate-y-[20%]"
          :color="
            activeSeasonContentForSchoolLevel.schoolLevel.slug === 'onderbouw'
              ? '#127b5d'
              : '#6c44a5'
          "
        />
        <MediaMusicLine
          class="pointer-events-none absolute bottom-0 right-0 translate-x-[0%] translate-y-[-5%]"
          :color="
            activeSeasonContentForSchoolLevel.schoolLevel.slug === 'onderbouw'
              ? '#127b5d'
              : '#6c44a5'
          "
        />
        <transition-fade :duration="200">
          <div
            v-if="fadeOutLeft"
            class="absolute inset-y-0 left-0 w-16 fade-out-left pointer-events-none"
          ></div>
        </transition-fade>
        <transition-fade :duration="200">
          <div
            v-if="fadeOutRight"
            class="absolute inset-y-0 right-0 w-16 fade-out-right pointer-events-none"
          ></div>
        </transition-fade>
      </div>
      <div v-else class="flex h-full justify-center items-center">
        <box-generic header="Geen materiaal">
          <p class="mt-3 leading-relaxed">
            In deze aflevering zijn er geen liedjes beschikbaar.<br />
            U kunt altijd liedjes vinden in het
            <router-link-button :to="{ name: 'MusicArchive' }" class="my-1">
              Muziekarchief</router-link-button
            >.
          </p>
        </box-generic>
      </div>
    </transition-fade>
  </template>
</template>

<script>
import { mapGetters, mapState } from "vuex";

import { RouterLink, RouterView } from "vue-router";

import BoxGeneric from "../../../../components/content/BoxGeneric.vue";
import RouterLinkButton from "../../../../components/content/RouterLinkButton.vue";
import TransitionFade from "../../../../components/magic/TransitionFade.vue";
import MediaMusicCircle from "../../../../components/content/svg/MediaMusicCircle.vue";
import MediaMusicLine from "../../../../components/content/svg/MediaMusicLine.vue";

export default {
  name: "SongList",
  components: {
    BoxGeneric,
    RouterView,
    RouterLink,
    RouterLinkButton,
    TransitionFade,
    MediaMusicCircle,
    MediaMusicLine,
  },
  data() {
    return {
      scrollPosition: undefined,
      scrollMax: undefined,
    };
  },
  computed: {
    ...mapState(["route"]),
    ...mapGetters([
      "activeVolume",
      "activeSeason",
      "activeSeasonContentForSchoolLevel",
      "routedSong",
    ]),
    scrollPercent() {
      // Only available after mounted.
      if (!this.scrollPosition === undefined) {
        return 0;
      }
      return (this.scrollPosition / this.scrollMax) * 100;
    },
    fadeOutLeft() {
      return this.scrollPercent > 0;
    },
    fadeOutRight() {
      return this.scrollPercent < 100;
    },
  },
  watch: {
    activeSeasonContentForSchoolLevel() {
      // Make sure to re-set `scrollMax`: The number of songs has probably changed.
      // The width might thus have changed. The `scrollPercent` must be re-calculated.
      // What is a 100% scroll to the side on a short list of songs, will be only
      // a 60% scroll on a much longer list of songs.
      this.$nextTick(() => {
        this.onScroll();
      });
    },
  },
  mounted() {
    this.onScroll();
  },
  methods: {
    onScroll() {
      // Only if list view is shown (= if no routed song). Otherwise return.
      if (!this.$refs.songList) {
        return;
      }

      this.scrollPosition = Math.ceil(this.$refs.songList.scrollLeft);
      this.scrollMax =
        this.$refs.songList?.scrollWidth - this.$refs.songList?.clientWidth;
    },
  },
};
</script>

<style scoped lang="css">
.fade-out-left {
  background-image: linear-gradient(
    to left,
    rgba(255, 255, 255, 0),
    rgba(255, 255, 255, 0.75) 40%,
    rgba(255, 255, 255, 0.95) 60%,
    white
  );
}

.fade-out-right {
  background-image: linear-gradient(
    to right,
    rgba(255, 255, 255, 0),
    rgba(255, 255, 255, 0.75) 40%,
    rgba(255, 255, 255, 0.95) 60%,
    white
  );
}
</style>
